import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  List,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { FileSearchOutlined, SearchOutlined } from "@ant-design/icons";
import type { ReportDraft, ReportGuidanceItem } from "./reportDraftApi";
import {
  buildReportGuidanceRows,
  coverageLabels,
  filterReportGuidanceRows,
  guidanceCents,
  guidanceMoneyText,
  visibleGuidanceItems,
} from "./reportGuidanceState";
import type { GuidanceFilter, GuidanceTableRow } from "./reportGuidanceState";

const { Text, Paragraph } = Typography;

function Basis({ item }: { item: ReportGuidanceItem }) {
  return (
    <Tag color={item.basis === "Audited" ? "blue" : "default"}>
      {item.basis === "Audited"
        ? "2026-10-14-ний шалгалт"
        : "Баримтаар тодруулах"}
    </Tag>
  );
}

export function ReportGuidanceDetails({
  items,
  stale = false,
}: {
  items: ReportGuidanceItem[];
  stale?: boolean;
}) {
  const visible = visibleGuidanceItems(items, stale);
  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {stale && (
        <Alert
          showIcon
          type="warning"
          message="Одоогийн эх, хувилбарыг баталгаажуулж тайлбарыг дахин тулгана. Өмнөх шалгалтын дүгнэлтийг түр нуусан."
        />
      )}
      {!visible.length && (
        <Alert
          showIcon
          type="info"
          message="Энэ мөрийн шалтгааны тайлбар одоогоор алга."
          description="Тайлбаргүй байгаа нь зөрчилгүй эсвэл хяналт дууссан гэсэн үг биш. Эх мөр, дүн болон шалгалтын жагсаалтыг нягтална уу."
        />
      )}
      {visible.map((item) => (
        <Card
          key={`${item.code}-${item.field}-${item.basis}`}
          size="small"
          title={
            <Space wrap>
              <span>{item.title}</span>
              <Tag
                color={
                  item.severity === "error"
                    ? "red"
                    : item.severity === "warning"
                      ? "orange"
                      : "default"
                }
              >
                {item.severity === "error"
                  ? "Засах"
                  : item.severity === "warning"
                    ? "Анхаарах"
                    : "Тайлбар"}
              </Tag>
            </Space>
          }
        >
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Basis item={item} />
            <div>
              <Text strong>Шалтгаан / тодруулах зүйл</Text>
              <Paragraph style={{ marginBottom: 0 }}>
                {item.explanation}
              </Paragraph>
            </div>
            <div>
              <Text strong>Хийх ажил</Text>
              <Paragraph style={{ marginBottom: 0 }}>{item.action}</Paragraph>
            </div>
            <div>
              <Text strong>Хариуцагч: </Text>
              <Text>{item.owner || "Хариуцагчийг тодруулах"}</Text>
            </div>
            <div>
              <Text strong>Шаардагдах баримт</Text>
              {item.evidenceRequired.length ? (
                <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                  {item.evidenceRequired.map((entry) => (
                    <li key={entry}>{entry}</li>
                  ))}
                </ul>
              ) : (
                <div>
                  <Text type="secondary">
                    Шаардагдах баримтыг хянагч тодруулна.
                  </Text>
                </div>
              )}
            </div>
            {item.evidence.length > 0 && (
              <div>
                <Text strong>Тулгасан эхийн заалт</Text>
                <List
                  size="small"
                  dataSource={item.evidence}
                  renderItem={(entry) => (
                    <List.Item style={{ padding: "4px 0" }}>
                      <Space direction="vertical" size={0}>
                        <Text>{entry.label}</Text>
                        <Text
                          type="secondary"
                          style={{ overflowWrap: "anywhere" }}
                        >
                          {entry.reference}
                        </Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </Space>
        </Card>
      ))}
    </Space>
  );
}

export function ReportGuidanceSummary({
  items,
  onOpen,
  stale = false,
}: {
  items: ReportGuidanceItem[];
  onOpen: () => void;
  stale?: boolean;
}) {
  const visible = visibleGuidanceItems(items, stale);
  const first = visible[0];
  return (
    <Space direction="vertical" size={3} style={{ maxWidth: 440 }}>
      {first ? (
        <>
          <Text strong>{first.title}</Text>
          <Paragraph
            ellipsis={{ rows: 2, tooltip: first.explanation }}
            style={{ marginBottom: 0 }}
          >
            {first.explanation}
          </Paragraph>
          <Paragraph
            type="secondary"
            ellipsis={{ rows: 2, tooltip: first.action }}
            style={{ marginBottom: 0 }}
          >
            Хийх ажил: {first.action}
          </Paragraph>
        </>
      ) : (
        <Text type="secondary">
          Шалтгааны тайлбар алга. Хяналт дууссан гэж үзэхгүй.
        </Text>
      )}
      <Button
        type="link"
        size="small"
        icon={<FileSearchOutlined />}
        onClick={onOpen}
        aria-label="Шалтгаан, хийх ажил болон баримтыг дэлгэрүүлж харах"
        style={{ paddingLeft: 0 }}
      >
        {visible.length
          ? `${visible.length} тайлбар · Баримт, хийх ажил`
          : "Баримт, хийх ажлыг харах"}
      </Button>
    </Space>
  );
}

function Comparisons({ row }: { row: GuidanceTableRow }) {
  // Derived totals remain checks. They do not enter the bond + stock subtotal.
  const visible = row.comparisons.filter(
    (item) =>
      !item.derived ||
      (item.delta !== null && guidanceCents(item.delta) !== 0n),
  );
  return (
    <div>
      <table
        aria-label={`${row.displayName}: Excel ба хадгалсан тайлангийн дүн`}
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 12,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <thead>
          <tr>
            <th
              scope="col"
              style={{ textAlign: "left", padding: "3px 8px 3px 0" }}
            >
              Бүрэлдэхүүн
            </th>
            <th scope="col" style={{ textAlign: "right", padding: 3 }}>
              Excel
            </th>
            <th scope="col" style={{ textAlign: "right", padding: 3 }}>
              Хадгалсан
            </th>
            <th scope="col" style={{ textAlign: "right", padding: 3 }}>
              Зөрүү
            </th>
          </tr>
        </thead>
        <tbody>
          {visible.map((item) => (
            <tr key={item.field}>
              <th
                scope="row"
                style={{
                  fontWeight: 400,
                  textAlign: "left",
                  padding: "3px 8px 3px 0",
                }}
              >
                {item.label}
              </th>
              <td
                style={{ textAlign: "right", padding: 3, whiteSpace: "nowrap" }}
              >
                {guidanceMoneyText(item.excelValue)}
              </td>
              <td
                style={{ textAlign: "right", padding: 3, whiteSpace: "nowrap" }}
              >
                {guidanceMoneyText(item.savedValue)}
              </td>
              <td
                style={{ textAlign: "right", padding: 3, whiteSpace: "nowrap" }}
              >
                <Text
                  type={
                    item.delta === null
                      ? "secondary"
                      : guidanceCents(item.delta) !== 0n
                        ? "danger"
                        : undefined
                  }
                >
                  {guidanceMoneyText(item.delta, true)}
                </Text>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th
              colSpan={3}
              scope="row"
              style={{
                textAlign: "left",
                paddingTop: 6,
                borderTop: "1px solid #f0f0f0",
              }}
            >
              Бонд + хувьцааны зөрүү
            </th>
            <td
              style={{
                textAlign: "right",
                paddingTop: 6,
                borderTop: "1px solid #f0f0f0",
                whiteSpace: "nowrap",
              }}
            >
              <Text strong>{guidanceMoneyText(row.assetDelta, true)}</Text>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function ReportGuidanceTable({
  report,
  stale = false,
  onOpen,
}: {
  report: ReportDraft;
  stale?: boolean;
  onOpen: (requestId: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<GuidanceFilter>("all");
  const effectiveStale = stale || report.sourceChanged;
  const rows = useMemo(
    () => buildReportGuidanceRows(report, effectiveStale),
    [report, effectiveStale],
  );
  const filtered = useMemo(
    () => filterReportGuidanceRows(rows, search, filter),
    [rows, search, filter],
  );
  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Alert
        showIcon
        type={effectiveStale ? "warning" : "info"}
        message="Зөрүү = хадгалсан тайлан − Excel"
        description={
          effectiveStale
            ? "Одоогийн эх, хувилбарыг баталгаажуулах шаардлагатай. Хадгалаагүй засвар эсвэл шинэ эх энэ хүснэгтэд тусгагдаагүй байж болно. Өмнөх шалгалтын дүгнэлтийг түр нуусан; эхийг амжилттай уншиж дахин хянана уу."
            : "Гараар нөхсөн утга хадгалсан тайланд орж болно. Энэ зөрүүг системийн баталгаажсан дүнгийн зөрүү гэж автоматаар үзэхгүй. Бонд + хувьцааны нийлбэрт бэлэн мөнгө, банкны шимтгэл, нийт/шилжүүлэх дүнгийн давхар шалгалт ороогүй."
        }
      />
      <Space wrap style={{ width: "100%" }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Нэр, email, тайлбараар хайх"
          aria-label="Харилцагч эсвэл хийх ажлаар хайх"
          style={{ width: 320, maxWidth: "100%" }}
        />
        <Select<GuidanceFilter>
          value={filter}
          onChange={setFilter}
          aria-label="Зөрүү ба хийх ажлын шүүлтүүр"
          style={{ minWidth: 240 }}
          options={[
            { value: "all", label: "Бүх хүсэлт" },
            { value: "difference", label: "Дүнгийн зөрүүтэй" },
            { value: "unlinked", label: "Excel холбоос тодорхойгүй" },
            { value: "missing", label: "Харьцуулах дүн дутуу" },
            { value: "audited", label: "2026-10-14-ний шалгалттай" },
            { value: "unknown", label: "Шалтгааны тайлбаргүй" },
          ]}
        />
        <Text type="secondary" role="status" aria-live="polite">
          {filtered.length} / {rows.length} хүсэлт
        </Text>
      </Space>
      <Table<GuidanceTableRow>
        rowKey="requestId"
        size="small"
        bordered
        dataSource={filtered}
        scroll={{ x: 1150 }}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50"],
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Энэ шүүлтүүрт таарах хүсэлт алга. Энэ нь бүх зөрчил шийдэгдсэн гэсэн үг биш."
            />
          ),
        }}
        columns={[
          {
            title: "Харилцагч",
            key: "customer",
            width: 210,
            render: (_, row) => (
              <Space direction="vertical" size={3}>
                <Text strong>{row.displayName || "Нэр дутуу"}</Text>
                <Text type="secondary" style={{ overflowWrap: "anywhere" }}>
                  {row.email || "Email дутуу"}
                </Text>
                <Tag
                  color={row.coverage === "Matched" ? "default" : "orange"}
                  style={{ whiteSpace: "normal" }}
                >
                  {coverageLabels[row.coverage]}
                </Tag>
              </Space>
            ),
          },
          {
            title: "Excel ба хадгалсан тайлан · ₮",
            key: "amounts",
            width: 460,
            render: (_, row) => <Comparisons row={row} />,
          },
          {
            title: "Шалтгаан ба хийх ажил",
            key: "guidance",
            width: 360,
            render: (_, row) => (
              <ReportGuidanceSummary
                items={row.items}
                stale={effectiveStale}
                onOpen={() => onOpen(row.requestId)}
              />
            ),
          },
          {
            title: "Баримт",
            key: "evidence",
            width: 120,
            render: (_, row) => (
              <Button
                icon={<FileSearchOutlined />}
                onClick={() => onOpen(row.requestId)}
                aria-label={`${row.displayName || "Энэ хүсэлт"}: баримт, хийх ажлыг харах`}
              >
                Харах
              </Button>
            ),
          },
        ]}
      />
    </Space>
  );
}
